import { EntityModel } from '@/interfaces'
import {
  ALL_PRICING_RULES_QUERY,
  CREATE_PRICING_RULES_MUTATION,
  DELETE_PRICING_RULES_MUTATION,
  FORCE_DELETE_PRICING_RULES_MUTATION,
  SINGLE_PRICING_RULES_QUERY,
  UPDATE_PRICING_RULES_MUTATION,
} from './graphql'
import ratioAccommodationEntity from '@/entities/ratioAccommodation/entity'
import ratioFamilyPackageEntity from '@/entities/ratioFamilyPackage/entity'
import ratioAccommodationAddonEntity from '@/entities/ratioAccommodationAddon/entity'
import travelPricingAgeGroupEntity from '@/entities/travelPricingAgeGroup/entity'
import travelPricingAgeFilterEntity from '@/entities/travelPricingAgeFilter/entity'
import ratioTravelDateEntity from '@/entities/ratioTravelDate/entity'

const travelPricingRulesModel: EntityModel = {
  name: 'pricingRules',
  namePlural: 'pricingRules',
  uniqueName: 'travelPricingRules',
  uniqueNamePlural: 'travelPricingRules',
  path: 'travel/pricing/pricing_rules',
  baseUrl: '/travel/pricing/pricing_rules',
  translationPrefix: 'entities:travelPricingRules.',
  getDisplayName: (values: any) => {
    return 'Pricing Rules'
  },
  gql: {
    getAll: ALL_PRICING_RULES_QUERY,
    getSingle: SINGLE_PRICING_RULES_QUERY,
    create: CREATE_PRICING_RULES_MUTATION,
    update: UPDATE_PRICING_RULES_MUTATION,
    delete: DELETE_PRICING_RULES_MUTATION,
    forceDelete: FORCE_DELETE_PRICING_RULES_MUTATION,
  },
  attributes: {
    checkAccommodationTypes: {
      name: 'checkAccommodationTypes',
      label: 'entities:travelPricingRules.attributes.checkAccommodationTypes.label',
      type: 'value',
      dataType: 'boolean',
      defaultInput: true,
      inputType: 'switch',
      validationType: 'string',
      validations: [],
    },
    accommodations: {
      name: 'accommodations',
      label: 'entities:travelPricingRules.attributes.accommodations.label',
      type: 'entityCollection',
      dataType: '[any]',
      getEntity: () => {
        return ratioAccommodationEntity
      },
      defaultInput: true,
      inputType: 'text',
      validationType: 'string',
      validations: [],
    },
    familyPackages: {
      name: 'familyPackages',
      label: 'entities:travelPricingRules.attributes.familyPackages.label',
      type: 'entityCollection',
      dataType: '[any]',
      getEntity: () => {
        return ratioFamilyPackageEntity
      },
      defaultInput: true,
      inputType: 'text',
      validationType: 'string',
      validations: [],
    },
    checkAccommodationAddons: {
      name: 'checkAccommodationAddons',
      label: 'entities:travelPricingRules.attributes.checkAccommodationAddons.label',
      type: 'value',
      dataType: 'boolean',
      defaultInput: true,
      inputType: 'switch',
      validationType: 'string',
      validations: [],
    },
    accommodationAddons: {
      name: 'accommodationAddons',
      label: 'entities:travelPricingRules.attributes.accommodationAddons.label',
      type: 'entityCollection',
      dataType: '[any]',
      getEntity: () => {
        return ratioAccommodationAddonEntity
      },
      defaultInput: false,
      inputType: 'text',
      validationType: 'string',
      validations: [],
    },
    checkAgeGroups: {
      name: 'checkAgeGroups',
      label: 'entities:travelPricingRules.attributes.checkAgeGroups.label',
      type: 'value',
      dataType: 'boolean',
      defaultInput: true,
      inputType: 'switch',
      validationType: 'string',
      validations: [],
    },
    ageGroups: {
      name: 'ageGroups',
      label: 'entities:travelPricingRules.attributes.ageGroups.label',
      type: 'entityCollection',
      dataType: '[any]',
      getEntity: () => {
        return travelPricingAgeGroupEntity
      },
      defaultInput: true,
      inputType: 'text',
      validationType: 'string',
      validations: [],
    },
    useAgeFilter: {
      name: 'useAgeFilter',
      label: 'entities:travelPricingRules.attributes.useAgeFilter.label',
      type: 'value',
      dataType: 'boolean',
      defaultInput: true,
      inputType: 'switch',
      validationType: 'string',
      validations: [
        {
          type: 'required',
        },
      ],
    },
    ageFilter: {
      name: 'ageFilter',
      label: 'entities:travelPricingRules.attributes.ageFilter.label',
      type: 'entityObject',
      dataType: 'any',
      getEntity: () => {
        return travelPricingAgeFilterEntity
      },
      defaultInput: false,
      inputType: 'select',
      validationType: 'string',
      validations: [],
    },
    checkDateRange: {
      name: 'checkDateRange',
      label: 'entities:travelPricingRules.attributes.checkDateRange.label',
      type: 'value',
      dataType: 'boolean',
      defaultInput: true,
      inputType: 'switch',
      validationType: 'string',
      validations: [],
    },
    startDate: {
      name: 'startDate',
      label: 'entities:travelPricingRules.attributes.startDate.label',
      type: 'value',
      dataType: 'Date',
      defaultInput: true,
      inputType: 'text',
      isStartDate: true,
      endDateAttribute: 'endDate',
      validationType: 'string',
      validations: [],
    },
    endDate: {
      name: 'endDate',
      label: 'entities:travelPricingRules.attributes.endDate.label',
      type: 'value',
      dataType: 'Date',
      defaultInput: true,
      inputType: 'text',
      isEndDate: true,
      startDateAttribute: 'startDate',
      validationType: 'string',
      validations: [],
    },
    checkTravelDate: {
      name: 'checkTravelDate',
      label: 'entities:travelPricingRules.attributes.checkTravelDate.label',
      type: 'value',
      dataType: 'boolean',
      defaultInput: true,
      inputType: 'switch',
      validationType: 'string',
      validations: [],
    },
    travelDates: {
      name: 'travelDates',
      label: 'entities:travelPricingRules.attributes.travelDates.label',
      type: 'entityCollection',
      dataType: '[any]',
      getEntity: () => {
        return ratioTravelDateEntity
      },
      defaultInput: false,
      inputType: 'text',
      validationType: 'string',
      validations: [],
    },
  },
}

export default travelPricingRulesModel
